import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Save, Loader2, Lock } from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import { useProfileStore, type ProfileBasicInfo, type ProfilePhysicalInfo, type ProfileDonationPolicy, type ProfileDoneePolicy } from '@/stores/profileStore';
import { useVerificationStore } from '@/stores/verificationStore';
import { useToastStore } from '@/stores/toastStore';
import {
  Card,
  CardContent,
  Button,
  Input,
  Select,
  Textarea,
  CheckboxGroup,
  ImageUpload,
  MultiImageUpload,
  Loading,
} from '@/components/ui';
import {
  PREFECTURES,
  BLOOD_TYPES,
  EDUCATION_LEVELS,
  EDUCATION_STATUSES,
  NATIONALITIES,
  TRAVEL_EXPENSE_OPTIONS,
  ANNUAL_INCOME_OPTIONS,
  MARITAL_STATUSES,
  ANCESTRIES,
  BODY_TYPES,
  HAIR_TYPES,
  HAIR_COLORS,
  EYELIDS,
  DONATION_METHODS,
  IDENTITY_DISCLOSURE_OPTIONS,
  PREFERRED_LOCATIONS,
} from '@kulum/shared';

type TabType = 'basic' | 'physical' | 'policy' | 'freetext' | 'images';

export function ProfileEditPage() {
  const { user, userData, fetchUserData } = useAuthStore();
  const { 
    profile, 
    isLoading, 
    isSaving, 
    isUploading,
    fetchProfile,
    updateBasicInfo,
    updatePhysicalInfo,
    updateDonationPolicy,
    updateDoneePolicy,
    updateFreeText,
    uploadProfileImage,
    uploadAdditionalImage,
    removeAdditionalImage,
  } = useProfileStore();
  const { verification, fetchVerification } = useVerificationStore();
  const { addToast } = useToastStore();

  const isDonor = userData?.role === 'donor';
  const [activeTab, setActiveTab] = useState<TabType>('basic');

  // Form states
  const [basicInfo, setBasicInfo] = useState<ProfileBasicInfo>({});
  const [physicalInfo, setPhysicalInfo] = useState<ProfilePhysicalInfo>({});
  const [donationPolicy, setDonationPolicy] = useState<ProfileDonationPolicy>({});
  const [doneePolicy, setDoneePolicy] = useState<ProfileDoneePolicy>({});
  const [freeText, setFreeText] = useState('');

  // Check verification status
  const isIdentityVerificationLocked =
    verification?.identity?.status === 'pending' ||
    verification?.identity?.status === 'approved';
  const isEducationVerified = verification?.education?.status === 'approved';
  const isIncomeVerified = verification?.income?.status === 'approved';
  const isReviewLocked = isDonor && (userData?.status === 'under_review' || userData?.status === 'approved');
  const isIdentityReviewLocked =
    isIdentityVerificationLocked ||
    userData?.identityVerificationStatus === 'pending' ||
    userData?.identityVerificationStatus === 'verified' ||
    userData?.identityVerified === true;
  const isBirthDateLocked = isReviewLocked || isIdentityReviewLocked;
  const isEducationLocked = isEducationVerified || isReviewLocked;
  const isIncomeLocked = isIncomeVerified || isReviewLocked;

  // Load profile and verification data
  useEffect(() => {
    if (user?.uid) {
      fetchProfile(user.uid);
      fetchVerification(user.uid);
    }
  }, [user?.uid, fetchProfile, fetchVerification]);

  // Populate form with profile data
  useEffect(() => {
    if (profile) {
      setBasicInfo(profile.basicInfo || {});
      setPhysicalInfo(profile.physicalInfo || {});
      setDonationPolicy(profile.donationPolicy || {});
      setDoneePolicy(profile.doneePolicy || {});
      setFreeText(profile.freeText || '');
    }
  }, [profile]);

  const handleSaveBasicInfo = async () => {
    if (!user?.uid) return;
    try {
      await updateBasicInfo(user.uid, basicInfo);
      addToast('success', '基本情報を保存しました');
    } catch {
      addToast('error', '保存に失敗しました');
    }
  };

  const handleSavePhysicalInfo = async () => {
    if (!user?.uid) return;
    try {
      await updatePhysicalInfo(user.uid, physicalInfo);
      addToast('success', '身体情報を保存しました');
    } catch {
      addToast('error', '保存に失敗しました');
    }
  };

  const handleSaveDonationPolicy = async () => {
    if (!user?.uid) return;
    try {
      await updateDonationPolicy(user.uid, donationPolicy);
      addToast('success', '提供方針を保存しました');
    } catch {
      addToast('error', '保存に失敗しました');
    }
  };

  const handleSaveDoneePolicy = async () => {
    if (!user?.uid) return;
    try {
      await updateDoneePolicy(user.uid, doneePolicy);
      addToast('success', '依頼方針を保存しました');
    } catch {
      addToast('error', '保存に失敗しました');
    }
  };

  const handleSaveFreeText = async () => {
    if (!user?.uid) return;
    try {
      await updateFreeText(user.uid, freeText);
      addToast('success', '自由記入欄を保存しました');
    } catch {
      addToast('error', '保存に失敗しました');
    }
  };

  const handleUploadProfileImage = async (file: File) => {
    if (!user?.uid) return '';
    const url = await uploadProfileImage(user.uid, file);
    await fetchUserData(user.uid);
    addToast('success', 'プロフィール画像を更新しました');
    return url;
  };

  const handleUploadAdditionalImage = async (file: File) => {
    if (!user?.uid) return '';
    const url = await uploadAdditionalImage(user.uid, file);
    addToast('success', '画像をアップロードしました');
    return url;
  };

  const handleRemoveAdditionalImage = async (imageUrl: string) => {
    if (!user?.uid) return;
    try {
      await removeAdditionalImage(user.uid, imageUrl);
      addToast('success', '画像を削除しました');
    } catch {
      addToast('error', '削除に失敗しました');
    }
  };

  // Tab configuration
  const tabs: { id: TabType; label: string; show: boolean }[] = [
    { id: 'basic', label: '基本情報', show: true },
    { id: 'physical', label: '身体情報', show: isDonor },
    { id: 'policy', label: isDonor ? '提供方針' : '依頼方針', show: true },
    { id: 'freetext', label: '自己紹介', show: true },
    { id: 'images', label: '画像', show: true },
  ];

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-6">
        <Loading message="読み込み中..." />
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6">
      <Link
        to="/mypage"
        className="inline-flex items-center gap-2 text-neutral-600 hover:text-copper mb-6 transition"
      >
        <ArrowLeft className="w-4 h-4" />
        <span className="text-sm">プロフィールに戻る</span>
      </Link>

      <h1 className="text-2xl font-heading-luxury text-luxury-dark mb-6">
        プロフィール編集
      </h1>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 overflow-x-auto pb-2">
        {tabs
          .filter((tab) => tab.show)
          .map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`
                px-4 py-2 text-sm font-medium rounded-lg whitespace-nowrap transition
                ${activeTab === tab.id
                  ? 'bg-copper text-white'
                  : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
                }
              `}
            >
              {tab.label}
            </button>
          ))}
      </div>

      {/* Tab Content */}
      <Card>
        <CardContent>
          {activeTab === 'basic' && (
            <BasicInfoForm
              value={basicInfo}
              onChange={setBasicInfo}
              onSave={handleSaveBasicInfo}
              isSaving={isSaving}
              isDonor={isDonor}
              isBirthDateLocked={isBirthDateLocked}
              isEducationLocked={isEducationLocked}
              isIncomeLocked={isIncomeLocked}
            />
          )}

          {activeTab === 'physical' && isDonor && (
            <PhysicalInfoForm
              value={physicalInfo}
              onChange={setPhysicalInfo}
              onSave={handleSavePhysicalInfo}
              isSaving={isSaving}
            />
          )}

          {activeTab === 'policy' && isDonor && (
            <DonationPolicyForm
              value={donationPolicy}
              onChange={setDonationPolicy}
              onSave={handleSaveDonationPolicy}
              isSaving={isSaving}
            />
          )}

          {activeTab === 'policy' && !isDonor && (
            <DoneePolicyForm
              value={doneePolicy}
              onChange={setDoneePolicy}
              onSave={handleSaveDoneePolicy}
              isSaving={isSaving}
            />
          )}

          {activeTab === 'freetext' && (
            <FreeTextForm
              value={freeText}
              onChange={setFreeText}
              onSave={handleSaveFreeText}
              isSaving={isSaving}
            />
          )}

          {activeTab === 'images' && (
            <ImagesForm
              profileImageUrl={userData?.iconUrl}
              additionalImages={profile?.images || []}
              onUploadProfileImage={handleUploadProfileImage}
              onUploadAdditionalImage={handleUploadAdditionalImage}
              onRemoveAdditionalImage={handleRemoveAdditionalImage}
              isUploading={isUploading}
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// Form Components
interface BasicInfoFormProps {
  value: ProfileBasicInfo;
  onChange: (value: ProfileBasicInfo) => void;
  onSave: () => Promise<void>;
  isSaving: boolean;
  isDonor: boolean;
  isBirthDateLocked: boolean;
  isEducationLocked: boolean;
  isIncomeLocked: boolean;
}

function BasicInfoForm({
  value,
  onChange,
  onSave,
  isSaving,
  isDonor,
  isBirthDateLocked,
  isEducationLocked,
  isIncomeLocked,
}: BasicInfoFormProps) {
  const handleChange = <K extends keyof ProfileBasicInfo>(key: K, val: ProfileBasicInfo[K]) => {
    onChange({ ...value, [key]: val });
  };

  const handleEducationChange = (field: string, val: string) => {
    onChange({
      ...value,
      education: {
        ...value.education,
        [field]: val,
      },
    });
  };

  const handleChildrenChange = (field: 'sons' | 'daughters', val: number) => {
    onChange({
      ...value,
      children: {
        sons: value.children?.sons || 0,
        daughters: value.children?.daughters || 0,
        [field]: val,
      },
    });
  };

  // Format date for input
  const birthDateValue = value.birthDate
    ? new Date(value.birthDate).toISOString().split('T')[0]
    : '';

  // ドニーの場合は簡素化フォーム
  if (!isDonor) {
    return (
      <div className="space-y-6">
        <h2 className="text-lg font-bold text-luxury-dark">基本情報</h2>
        <p className="text-sm text-neutral-600">
          ドニーのプロフィールに必要な基本情報を入力してください。
        </p>

        <div>
          <div className="flex items-center gap-2 mb-2">
            <h3 className="text-sm font-medium text-neutral-700">本人確認情報</h3>
            {isBirthDateLocked && (
              <span className="inline-flex items-center gap-1 text-xs text-green-600 bg-green-50 px-2 py-0.5 rounded-full">
                <Lock className="w-3 h-3" />
                ロック中
              </span>
            )}
          </div>
          {isBirthDateLocked && (
            <p className="text-xs text-amber-600 mb-3">
              ※ 本人確認の審査中または認証済みのため、生年月日は変更できません。変更が必要な場合は管理者にお問い合わせください。
            </p>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Input
              label="生年月日"
              type="date"
              value={birthDateValue}
              onChange={(e) => handleChange('birthDate', e.target.value ? new Date(e.target.value) : undefined)}
              disabled={isBirthDateLocked}
              required
            />
            <p className="text-xs text-neutral-500 mt-1">
              ※ 年齢は「○代前半/後半」として表示されます
            </p>
          </div>

          <Select
            label="居住地"
            value={value.residence || ''}
            onChange={(e) => handleChange('residence', e.target.value)}
            options={PREFECTURES.map((p) => ({ value: p, label: p }))}
            placeholder="選択してください"
            required
          />
        </div>

        <div className="flex justify-end">
          <Button onClick={onSave} disabled={isSaving} leftIcon={isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}>
            {isSaving ? '保存中...' : '保存する'}
          </Button>
        </div>
      </div>
    );
  }

  // ドナーの場合は完全フォーム
  return (
    <div className="space-y-6">
      <h2 className="text-lg font-bold text-luxury-dark">基本情報</h2>

      <div>
        <div className="flex items-center gap-2 mb-2">
          <h3 className="text-sm font-medium text-neutral-700">本人確認情報</h3>
          {isBirthDateLocked && (
            <span className="inline-flex items-center gap-1 text-xs text-green-600 bg-green-50 px-2 py-0.5 rounded-full">
              <Lock className="w-3 h-3" />
              ロック中
            </span>
          )}
        </div>
        {isBirthDateLocked && (
          <p className="text-xs text-amber-600 mb-3">
            ※ 本人確認の審査中または認証済みのため、生年月日は変更できません。変更が必要な場合は管理者にお問い合わせください。
          </p>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input
          label="生年月日"
          type="date"
          value={birthDateValue}
          onChange={(e) => handleChange('birthDate', e.target.value ? new Date(e.target.value) : undefined)}
          disabled={isBirthDateLocked}
          required
        />

        <Select
          label="国籍"
          value={value.nationality || ''}
          onChange={(e) => handleChange('nationality', e.target.value as ProfileBasicInfo['nationality'])}
          options={NATIONALITIES.map((n) => ({ value: n, label: n }))}
          placeholder="選択してください"
          required
        />

        <Select
          label="血液型"
          value={value.bloodType || ''}
          onChange={(e) => handleChange('bloodType', e.target.value as ProfileBasicInfo['bloodType'])}
          options={BLOOD_TYPES.map((t) => ({ value: t, label: `${t}型` }))}
          placeholder="選択してください"
          required
        />

        <Select
          label="Rh型"
          value={value.bloodTypeRh || ''}
          onChange={(e) => handleChange('bloodTypeRh', e.target.value as ProfileBasicInfo['bloodTypeRh'])}
          options={[
            { value: '+', label: 'Rh+' },
            { value: '-', label: 'Rh-' },
            { value: 'unknown', label: '不明' },
          ]}
          placeholder="選択してください"
          required
        />

        <Select
          label="出身地"
          value={value.birthplace || ''}
          onChange={(e) => handleChange('birthplace', e.target.value)}
          options={PREFECTURES.map((p) => ({ value: p, label: p }))}
          placeholder="選択してください"
          required
        />

        <Select
          label="居住地"
          value={value.residence || ''}
          onChange={(e) => handleChange('residence', e.target.value)}
          options={PREFECTURES.map((p) => ({ value: p, label: p }))}
          placeholder="選択してください"
          required
        />

        <Select
          label="婚姻状況"
          value={value.maritalStatus || ''}
          onChange={(e) => handleChange('maritalStatus', e.target.value as ProfileBasicInfo['maritalStatus'])}
          options={MARITAL_STATUSES.map((s) => ({ value: s, label: s }))}
          placeholder="選択してください"
          required
        />

        <Select
          label="血統"
          value={value.ancestry || ''}
          onChange={(e) => handleChange('ancestry', e.target.value as ProfileBasicInfo['ancestry'])}
          options={ANCESTRIES.map((a) => ({ value: a, label: a }))}
          placeholder="選択してください"
        />
      </div>

      {/* Education */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <h3 className="text-sm font-medium text-neutral-700">学歴情報</h3>
          {isEducationLocked && (
            <span className="inline-flex items-center gap-1 text-xs text-green-600 bg-green-50 px-2 py-0.5 rounded-full">
              <Lock className="w-3 h-3" />
              ロック中
            </span>
          )}
        </div>
        {isEducationLocked && (
          <p className="text-xs text-amber-600 mb-3">
            ※ 審査中または認証済みのため、この項目は変更できません。変更が必要な場合は管理者にお問い合わせください。
          </p>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Select
            label="最終学歴"
            value={value.education?.level || ''}
            onChange={(e) => handleEducationChange('level', e.target.value)}
            options={EDUCATION_LEVELS.map((l) => ({ value: l, label: l }))}
            placeholder="選択してください"
            disabled={isEducationLocked}
          />

          <Input
            label="学校名"
            value={value.education?.schoolName || ''}
            onChange={(e) => handleEducationChange('schoolName', e.target.value)}
            placeholder="○○大学"
            disabled={isEducationLocked}
          />

          <Input
            label="学部・学科"
            value={value.education?.faculty || ''}
            onChange={(e) => handleEducationChange('faculty', e.target.value)}
            placeholder="○○学部"
            disabled={isEducationLocked}
          />

          <Select
            label="卒業状況"
            value={value.education?.status || ''}
            onChange={(e) => handleEducationChange('status', e.target.value)}
            options={EDUCATION_STATUSES.map((s) => ({ value: s, label: s }))}
            placeholder="選択してください"
            disabled={isEducationLocked}
          />
        </div>
      </div>

      {/* Annual Income */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <h3 className="text-sm font-medium text-neutral-700">年収情報</h3>
          {isIncomeLocked && (
            <span className="inline-flex items-center gap-1 text-xs text-green-600 bg-green-50 px-2 py-0.5 rounded-full">
              <Lock className="w-3 h-3" />
              ロック中
            </span>
          )}
        </div>
        {isIncomeLocked && (
          <p className="text-xs text-amber-600 mb-3">
            ※ 審査中または認証済みのため、この項目は変更できません。変更が必要な場合は管理者にお問い合わせください。
          </p>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Select
            label="年収"
            value={value.annualIncome || ''}
            onChange={(e) => handleChange('annualIncome', e.target.value as ProfileBasicInfo['annualIncome'])}
            options={ANNUAL_INCOME_OPTIONS.map((o) => ({ value: o, label: o }))}
            placeholder="選択してください"
            disabled={isIncomeLocked}
          />
        </div>
      </div>

      {/* Children */}
      <div>
        <h3 className="text-sm font-medium text-neutral-700 mb-3">お子様情報</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="息子の人数"
            type="number"
            value={value.children?.sons ?? 0}
            onChange={(e) => handleChildrenChange('sons', e.target.value ? Number(e.target.value) : 0)}
            placeholder="0"
            min={0}
            max={20}
          />

          <Input
            label="娘の人数"
            type="number"
            value={value.children?.daughters ?? 0}
            onChange={(e) => handleChildrenChange('daughters', e.target.value ? Number(e.target.value) : 0)}
            placeholder="0"
            min={0}
            max={20}
          />
        </div>
      </div>

      <div className="flex justify-end">
        <Button onClick={onSave} disabled={isSaving} leftIcon={isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}>
          {isSaving ? '保存中...' : '保存する'}
        </Button>
      </div>
    </div>
  );
}

interface PhysicalInfoFormProps {
  value: ProfilePhysicalInfo;
  onChange: (value: ProfilePhysicalInfo) => void;
  onSave: () => Promise<void>;
  isSaving: boolean;
}

function PhysicalInfoForm({ value, onChange, onSave, isSaving }: PhysicalInfoFormProps) {
  const handleChange = <K extends keyof ProfilePhysicalInfo>(key: K, val: ProfilePhysicalInfo[K]) => {
    onChange({ ...value, [key]: val });
  };

  return (
    <div className="space-y-6">
      <h2 className="text-lg font-bold text-luxury-dark">身体情報</h2>
      <p className="text-sm text-neutral-600">
        ドナーとしてのプロフィールに表示される身体的特徴です。
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input
          label="身長 (cm)"
          type="number"
          value={value.height || ''}
          onChange={(e) => handleChange('height', e.target.value ? Number(e.target.value) : undefined)}
          placeholder="170"
          min={100}
          max={250}
          required
        />

        <Input
          label="体重 (kg)"
          type="number"
          value={value.weight || ''}
          onChange={(e) => handleChange('weight', e.target.value ? Number(e.target.value) : undefined)}
          placeholder="65"
          min={30}
          max={200}
          required
        />

        <Select
          label="体型"
          value={value.bodyType || ''}
          onChange={(e) => handleChange('bodyType', e.target.value as ProfilePhysicalInfo['bodyType'])}
          options={BODY_TYPES.map((t) => ({ value: t, label: t }))}
          placeholder="選択してください"
          required
        />

        <Select
          label="髪質"
          value={value.hairType || ''}
          onChange={(e) => handleChange('hairType', e.target.value as ProfilePhysicalInfo['hairType'])}
          options={HAIR_TYPES.map((t) => ({ value: t, label: t }))}
          placeholder="選択してください"
        />

        <Select
          label="髪の色"
          value={value.hairColor || ''}
          onChange={(e) => handleChange('hairColor', e.target.value as ProfilePhysicalInfo['hairColor'])}
          options={HAIR_COLORS.map((c) => ({ value: c, label: c }))}
          placeholder="選択してください"
        />

        <Select
          label="まぶた"
          value={value.eyelid || ''}
          onChange={(e) => handleChange('eyelid', e.target.value as ProfilePhysicalInfo['eyelid'])}
          options={EYELIDS.map((e) => ({ value: e, label: e }))}
          placeholder="選択してください"
        />
      </div>

      <div className="flex justify-end">
        <Button onClick={onSave} disabled={isSaving} leftIcon={isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}>
          {isSaving ? '保存中...' : '保存する'}
        </Button>
      </div>
    </div>
  );
}

interface DonationPolicyFormProps {
  value: ProfileDonationPolicy;
  onChange: (value: ProfileDonationPolicy) => void;
  onSave: () => Promise<void>;
  isSaving: boolean;
}

function DonationPolicyForm({ value, onChange, onSave, isSaving }: DonationPolicyFormProps) {
  const handleChange = <K extends keyof ProfileDonationPolicy>(key: K, val: ProfileDonationPolicy[K]) => {
    onChange({ ...value, [key]: val });
  };

  return (
    <div className="space-y-6">
      <h2 className="text-lg font-bold text-luxury-dark">提供方針</h2>
      <p className="text-sm text-neutral-600">
        提供方法や条件を設定してください。
      </p>

      <CheckboxGroup
        label="提供方法"
        options={DONATION_METHODS.map((m) => ({ value: m.value, label: m.label }))}
        value={value.methods || []}
        onChange={(methods) => handleChange('methods', methods as ProfileDonationPolicy['methods'])}
        required
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input
          label="謝礼金 (円)"
          type="number"
          value={value.compensation || ''}
          onChange={(e) => handleChange('compensation', e.target.value ? Number(e.target.value) : undefined)}
          placeholder="0"
          min={0}
          hint="無償の場合は0を入力"
          required
        />

        <Select
          label="交通費"
          value={value.travelExpense || ''}
          onChange={(e) => handleChange('travelExpense', e.target.value as ProfileDonationPolicy['travelExpense'])}
          options={TRAVEL_EXPENSE_OPTIONS.filter((o) => o !== '選択してください').map((o) => ({ value: o, label: o }))}
          placeholder="選択してください"
        />

        <Select
          label="身元開示"
          value={value.identityDisclosure || ''}
          onChange={(e) => handleChange('identityDisclosure', e.target.value as ProfileDonationPolicy['identityDisclosure'])}
          options={IDENTITY_DISCLOSURE_OPTIONS.map((o) => ({ value: o, label: o }))}
          placeholder="選択してください"
        />

        <Select
          label="近親交配対策"
          value={value.consanguinityMeasures || ''}
          onChange={(e) => handleChange('consanguinityMeasures', e.target.value as ProfileDonationPolicy['consanguinityMeasures'])}
          options={[
            { value: '対策を講じている', label: '対策を講じている' },
            { value: '講じていない', label: '講じていない' },
          ]}
          placeholder="選択してください"
        />

        <Select
          label="出自を知る権利への対応"
          value={value.rightToKnowOrigin || ''}
          onChange={(e) => handleChange('rightToKnowOrigin', e.target.value as ProfileDonationPolicy['rightToKnowOrigin'])}
          options={[
            { value: '対応可能', label: '対応可能' },
            { value: '対応不可能', label: '対応不可能' },
          ]}
          placeholder="選択してください"
        />

        <Select
          label="同市家族への提供"
          value={value.sameCityFamilyDonation || ''}
          onChange={(e) => handleChange('sameCityFamilyDonation', e.target.value as ProfileDonationPolicy['sameCityFamilyDonation'])}
          options={[
            { value: '賛同する', label: '賛同する' },
            { value: '賛同しない', label: '賛同しない' },
          ]}
          placeholder="選択してください"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-neutral-700 mb-2">
          提供可能地域
          <span className="text-red-500 ml-1">*</span>
        </label>
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2 max-h-60 overflow-y-auto p-2 border rounded-lg">
          {PREFECTURES.map((pref) => (
            <label key={pref} className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={value.donationAreas?.includes(pref) || false}
                onChange={(e) => {
                  const areas = value.donationAreas || [];
                  if (e.target.checked) {
                    handleChange('donationAreas', [...areas, pref]);
                  } else {
                    handleChange('donationAreas', areas.filter((a) => a !== pref));
                  }
                }}
                className="rounded border-neutral-300 text-copper focus:ring-copper"
              />
              {pref}
            </label>
          ))}
        </div>
      </div>

      <div className="flex justify-end">
        <Button onClick={onSave} disabled={isSaving} leftIcon={isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}>
          {isSaving ? '保存中...' : '保存する'}
        </Button>
      </div>
    </div>
  );
}

interface DoneePolicyFormProps {
  value: ProfileDoneePolicy;
  onChange: (value: ProfileDoneePolicy) => void;
  onSave: () => Promise<void>;
  isSaving: boolean;
}

function DoneePolicyForm({ value, onChange, onSave, isSaving }: DoneePolicyFormProps) {
  const handleChange = <K extends keyof ProfileDoneePolicy>(key: K, val: ProfileDoneePolicy[K]) => {
    onChange({ ...value, [key]: val });
  };

  return (
    <div className="space-y-6">
      <h2 className="text-lg font-bold text-luxury-dark">依頼方針</h2>
      <p className="text-sm text-neutral-600">
        希望する提供方法や条件を設定してください。
      </p>

      <CheckboxGroup
        label="希望提供方法"
        options={DONATION_METHODS.map((m) => ({ value: m.value, label: m.label }))}
        value={value.methods || []}
        onChange={(methods) => handleChange('methods', methods as ProfileDoneePolicy['methods'])}
        required
      />

      <Select
        label="提供地域"
        value={value.preferredLocation || ''}
        onChange={(e) => handleChange('preferredLocation', e.target.value as ProfileDoneePolicy['preferredLocation'])}
        options={PREFERRED_LOCATIONS.map((l) => ({ value: l, label: l }))}
        placeholder="選択してください"
      />

      <div className="flex justify-end">
        <Button onClick={onSave} disabled={isSaving} leftIcon={isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}>
          {isSaving ? '保存中...' : '保存する'}
        </Button>
      </div>
    </div>
  );
}

interface FreeTextFormProps {
  value: string;
  onChange: (value: string) => void;
  onSave: () => Promise<void>;
  isSaving: boolean;
}

function FreeTextForm({ value, onChange, onSave, isSaving }: FreeTextFormProps) {
  return (
    <div className="space-y-6">
      <h2 className="text-lg font-bold text-luxury-dark">自由記入欄</h2>
      <p className="text-sm text-neutral-600">
        自己紹介や提供に対する思い、お相手へのメッセージなどを自由に記入してください。
      </p>

      <Textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="自己紹介や提供への思いなどを記入してください..."
        className="min-h-[200px]"
        maxLength={1000}
      />
      <p className="text-sm text-neutral-500 text-right">{value.length}/1000文字</p>

      <div className="flex justify-end">
        <Button onClick={onSave} disabled={isSaving} leftIcon={isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}>
          {isSaving ? '保存中...' : '保存する'}
        </Button>
      </div>
    </div>
  );
}

interface ImagesFormProps {
  profileImageUrl?: string;
  additionalImages: string[];
  onUploadProfileImage: (file: File) => Promise<string>;
  onUploadAdditionalImage: (file: File) => Promise<string>;
  onRemoveAdditionalImage: (imageUrl: string) => void;
  isUploading: boolean;
}

function ImagesForm({
  profileImageUrl,
  additionalImages,
  onUploadProfileImage,
  onUploadAdditionalImage,
  onRemoveAdditionalImage,
  isUploading,
}: ImagesFormProps) {
  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-lg font-bold text-luxury-dark mb-4">プロフィール画像</h2>
        <p className="text-sm text-neutral-600 mb-4">
          メインのプロフィール画像を設定してください。
        </p>
        <ImageUpload
          value={profileImageUrl}
          onUpload={onUploadProfileImage}
          variant="avatar"
          hint="推奨サイズ: 400x400px、5MB以下"
          disabled={isUploading}
        />
      </div>

      <div>
        <h2 className="text-lg font-bold text-luxury-dark mb-4">追加画像</h2>
        <p className="text-sm text-neutral-600 mb-4">
          プロフィールに表示される追加の画像です（最大5枚）。
        </p>
        <MultiImageUpload
          images={additionalImages}
          maxImages={5}
          onUpload={onUploadAdditionalImage}
          onRemove={onRemoveAdditionalImage}
          hint="各画像5MB以下"
          disabled={isUploading}
        />
      </div>
    </div>
  );
}
